import { ComponentProps, createContext, ReactNode, useContext } from 'react'

import { cn } from '@/shared/lib'

import { FilterChip } from './chip'

type FilterLayout = 'vertical' | 'horizontal' | 'mobile'

const FilterLayoutContext = createContext<FilterLayout>('vertical')

type FilterLayoutProviderProps = {
  layout: FilterLayout
  children: ReactNode
}

export const FilterLayoutProvider = ({
  layout,
  children,
}: FilterLayoutProviderProps) => {
  return (
    <FilterLayoutContext.Provider value={layout}>
      {children}
    </FilterLayoutContext.Provider>
  )
}

export const useFilterLayout = () => useContext(FilterLayoutContext)

type Props = {
  className?: string
  children: ReactNode
}

export const Title = ({ className, children }: Props) => {
  const layout = useFilterLayout()

  return (
    <h3
      className={cn(
        'typo-body1 font-semibold text-black',
        layout === 'horizontal' ? 'mb-0 w-[200px] shrink-0 self-start' : 'mb-3',
        className,
      )}
    >
      {children}
    </h3>
  )
}

export const SubTitle = ({ className, children }: Props) => {
  return (
    <h4
      className={cn(
        'typo-label1 mb-2 font-semibold text-purple-500',
        className,
      )}
    >
      {children}
    </h4>
  )
}

export const Description = ({ className, children }: Props) => {
  return (
    <p className={cn('typo-label2 font-normal text-gray-500', className)}>
      {children}
    </p>
  )
}

export const List = ({ className, children }: Props) => {
  const layout = useFilterLayout()

  return (
    <ul
      className={cn(
        'flex flex-wrap gap-2',
        layout === 'horizontal' && 'min-w-0 flex-1',
        className,
      )}
    >
      {children}
    </ul>
  )
}

export const Item = ({
  children,
  ...restProps
}: ComponentProps<typeof FilterChip> & Props) => {
  return (
    <li className="flex flex-wrap gap-2">
      <FilterChip {...restProps}>{children}</FilterChip>
    </li>
  )
}
